import React, { useEffect } from "react";
import styles from 'styles/components/ReportSummaryBody.module.sass';
import { TableOfContents } from "interface/ReportContent";
import ZeroneChart from "components/charts/ZeroneChart";
import { useParams } from "react-router-dom";
import { getReportChartData } from "api/ReportApiService";
import LegacyChart from "interface/LegacyChart";
import TableOfContentItem from "./TableOfContentItem";

const tag = 'ReportSummaryBody';

/**
 * Props for the ReportSummaryBody component.
 * 
 * @interface ReportSummaryBodyProps
 * @property {TableOfContents[]} tableOfContents - An array of TableOfContents objects.
 */
interface ReportSummaryBodyProps {
    tableOfContents: TableOfContents[];
    hasAccessToReport: boolean;
}

/**
 * ReportSummaryBody component displays a summary of the report including a table of contents.
 *
 * @component
 * @param {ReportSummaryBodyProps} props - The props for the component.
 * @param {Array<{title: string, description: string}>} props.tableOfContents - The table of contents data to be displayed.
 * @returns {JSX.Element} The rendered ReportSummaryBody component.
 */
const ReportSummaryBody: React.FC<ReportSummaryBodyProps> = ({ tableOfContents, hasAccessToReport }) => {
    const [chartData, setChartData] = React.useState<LegacyChart | null>(null);
    const { id } = useParams<{ id: UUID }>();

    useEffect(() => {
        const retrieveReportChart = async () => {
            if (id) {
                try {
                    const chartData = await getReportChartData({ tag, id });
                    if (chartData) {
                        setChartData(chartData);
                        console.log('Fetched report chart data:', chartData);
                    }
                } catch (error) {
                    console.error('Error fetching report chart:', error);
                }
            }
        }
        retrieveReportChart();
    }, [id])

    const renderInteractiveChart = () => {
        // If no chart data is available, return null
        if (!chartData || !chartData.series || chartData.series.length === 0) return null;
        return (
            <ZeroneChart
                key={chartData.title}
                chart={chartData}
            />

        );
    }

    return (
        <div className={styles['report-summary-body']}>
            {tableOfContents && tableOfContents.length > 0 && (
                <div className={styles['toc']}>
                    <h2 className={styles['title']}>Table of Contents</h2>
                    <div className={styles['toc-list']}>
                        {tableOfContents.map((toc, index) => (
                            <TableOfContentItem toc={toc} key={index} />
                        ))}
                    </div>
                </div>
            )}
            
            {chartData && (
                <div className={styles['showcase']}>
                    <div className={styles['showcase-content']}>
                        <div className={styles['blurb']} style={{ filter: hasAccessToReport ? 'none' : 'blur(5px)'}}>
                            {renderInteractiveChart()}
                        </div>
                    </div>
                        
                    <p className={styles['showcase-note']} style={{ display : hasAccessToReport ? 'none' : 'block' }}>
                        {hasAccessToReport ? 'View the full report for more insights.' : 'Unlock the full report to view more insights.'}
                    </p>
                </div>
            )}
        </div>
    );
}

export default ReportSummaryBody